import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

interface BusinessData {
  companyName: string;
  address: string;
  logo?: string;
  phone?: string;
  businessEmail?: string;
}

interface ClientData {
  clientName: string;
  clientEmail: string;
  customFields?: any[];
}

interface ProposalData {
  services: any[];
  materials: any[];
  selectedTaxes: any[];
  discountAmount: number;
  discountPercentage: number;
  workDuration: number;
  workDurationType: 'hours' | 'days';
  workStartDate: string;
  quotationValidityDate: string;
  subtotal: number;
  discount: number;
  taxes: number;
  total: number;
}

export const generateProposalPDF = async (
  businessData: BusinessData,
  clientData: ClientData,
  proposalData: ProposalData,
  language: string,
  businessUid: string,
  quotationId: string
): Promise<string> => {
  const doc = new jsPDF();
  
  // Configurar idioma
  const isSpanish = language === 'es';
  
  // Configuraciones de texto
  const texts = {
    proposal: isSpanish ? 'PROPUESTA' : 'PROPOSAL',
    client: isSpanish ? 'CLIENTE' : 'CLIENT',
    services: isSpanish ? 'SERVICIOS' : 'SERVICES',
    materials: isSpanish ? 'MATERIALES' : 'MATERIALS',
    subtotal: isSpanish ? 'Subtotal' : 'Subtotal',
    discounts: isSpanish ? 'Descuentos' : 'Discounts',
    taxes: isSpanish ? 'Impuestos' : 'Taxes',
    total: isSpanish ? 'Total' : 'Total',
    workDuration: isSpanish ? 'Duración del Trabajo' : 'Work Duration',
    workStartDate: isSpanish ? 'Fecha de Inicio' : 'Start Date',
    validityDate: isSpanish ? 'Fecha de Validez' : 'Validity Date',
    days: isSpanish ? 'Días' : 'Days',
    hours: isSpanish ? 'Horas' : 'Hours',
    description: isSpanish ? 'Descripción' : 'Description',
    quantity: isSpanish ? 'Cantidad' : 'Quantity',
    unitPrice: isSpanish ? 'Precio Unit.' : 'Unit Price',
    totalPrice: isSpanish ? 'Total' : 'Total',
    unit: isSpanish ? 'Unidad' : 'Unit'
  };

  let yPosition = 20;

  // Header con logo y datos de la empresa
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(texts.proposal, 105, yPosition, { align: 'center' });
  yPosition += 20;

  // Logo de la empresa (si existe)
  if (businessData.logo) {
    try {
      const img = new Image();
      img.src = businessData.logo;
      await new Promise((resolve) => {
        img.onload = () => {
          const imgWidth = 30;
          const imgHeight = (img.height * imgWidth) / img.width;
          doc.addImage(img, 'JPEG', 20, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;
          resolve(true);
        };
        img.onerror = () => resolve(false);
      });
    } catch (error) {
      console.error('Error loading logo:', error);
    }
  }

  // Datos de la empresa
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(businessData.companyName, 20, yPosition);
  yPosition += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(businessData.address, 20, yPosition);
  yPosition += 6;

  if (businessData.phone) {
    doc.text(`Tel: ${businessData.phone}`, 20, yPosition);
    yPosition += 6;
  }

  if (businessData.businessEmail) {
    doc.text(`Email: ${businessData.businessEmail}`, 20, yPosition);
    yPosition += 10;
  }

  // Datos del cliente
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(texts.client, 20, yPosition);
  yPosition += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nombre: ${clientData.clientName}`, 20, yPosition);
  yPosition += 6;
  doc.text(`Email: ${clientData.clientEmail}`, 20, yPosition);
  yPosition += 6;

  // Campos personalizados del formulario (en dos columnas)
  if (clientData.customFields && clientData.customFields.length > 0) {
    const leftColumn = clientData.customFields.filter((_, index) => index % 2 === 0);
    const rightColumn = clientData.customFields.filter((_, index) => index % 2 === 1);

    const maxRows = Math.max(leftColumn.length, rightColumn.length);
    
    for (let i = 0; i < maxRows; i++) {
      const leftField = leftColumn[i];
      const rightField = rightColumn[i];

      if (leftField) {
        const value = Array.isArray(leftField.value) ? leftField.value.join(', ') : leftField.value;
        doc.text(`${leftField.label}: ${value || '-'}`, 20, yPosition);
      }

      if (rightField) {
        const value = Array.isArray(rightField.value) ? rightField.value.join(', ') : rightField.value;
        doc.text(`${rightField.label}: ${value || '-'}`, 110, yPosition);
      }

      yPosition += 6;
    }
  }

  yPosition += 10;

  // Unificar servicios y materiales en una sola tabla
  const allItems = [
    ...((proposalData.services || []).map((item: any) => ({ ...item, _type: texts.services })) ),
    ...((proposalData.materials || []).map((item: any) => ({ ...item, _type: texts.materials })) )
  ];
  console.log('Items unificados para PDF:', allItems);
  if (allItems.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(isSpanish ? 'DETALLE DE SERVICIOS Y MATERIALES' : 'SERVICES AND MATERIALS DETAIL', 20, yPosition);
    yPosition += 8;

    const itemsData = allItems.map((item: any) => [
      item._type,
      item.name || '-',
      item.description || '-',
      item.quantity || 1,
      item.unit || '',
      `$${parseFloat(item.price || 0).toFixed(2)}`,
      `$${((parseFloat(item.price || 0) * (item.quantity || 1))).toFixed(2)}`
    ]);

    (doc as any).autoTable({
      startY: yPosition,
      head: [[
        isSpanish ? 'Tipo' : 'Type',
        texts.description,
        texts.unitPrice,
        texts.quantity,
        texts.unit,
        texts.unitPrice,
        texts.totalPrice
      ]],
      body: itemsData,
      theme: 'grid',
      headStyles: { fillColor: [100, 100, 255] },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 20 }, // Tipo
        1: { cellWidth: 38 }, // Nombre
        2: { cellWidth: 55 }, // Descripción
        3: { cellWidth: 15, halign: 'center' }, // Cantidad
        4: { cellWidth: 18, halign: 'center' }, // Unidad
        5: { cellWidth: 20, halign: 'right' }, // Precio unitario
        6: { cellWidth: 20, halign: 'right' }  // Total
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Información del trabajo
  if (proposalData.workDuration || proposalData.workStartDate || proposalData.quotationValidityDate) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMACIÓN DEL TRABAJO', 20, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    if (proposalData.workDuration) {
      const durationText = `${proposalData.workDuration} ${proposalData.workDurationType === 'hours' ? texts.hours : texts.days}`;
      doc.text(`${texts.workDuration}: ${durationText}`, 20, yPosition);
      yPosition += 6;
    }

    if (proposalData.workStartDate) {
      const startDate = new Date(proposalData.workStartDate).toLocaleDateString();
      doc.text(`${texts.workStartDate}: ${startDate}`, 20, yPosition);
      yPosition += 6;
    }

    if (proposalData.quotationValidityDate) {
      const validityDate = new Date(proposalData.quotationValidityDate).toLocaleDateString();
      doc.text(`${texts.validityDate}: ${validityDate}`, 20, yPosition);
      yPosition += 10;
    }
  }

  // Resumen de costos
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('RESUMEN DE COSTOS', 20, yPosition);
  yPosition += 8;

  const summaryData = [
    [texts.subtotal, `$${proposalData.subtotal.toFixed(2)}`],
    [texts.discounts, `-$${proposalData.discount.toFixed(2)}`],
    [`${texts.subtotal} con ${texts.discounts.toLowerCase()}`, `$${(proposalData.subtotal - proposalData.discount).toFixed(2)}`],
    [texts.taxes, `$${proposalData.taxes.toFixed(2)}`],
    [texts.total, `$${proposalData.total.toFixed(2)}`]
  ];

  (doc as any).autoTable({
    startY: yPosition,
    body: summaryData,
    theme: 'plain',
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 120, fontStyle: 'bold' },
      1: { cellWidth: 60, halign: 'right', fontStyle: 'bold' }
    },
    didParseCell: function(data: any) {
      if (data.row.index === summaryData.length - 1) {
        data.cell.styles.fontSize = 12;
        data.cell.styles.textColor = [220, 53, 69];
      }
    }
  });

  // Al final, en vez de descargar, subimos a Storage:
  const pdfBlob = doc.output('blob');
  const storagePath = `Quotations/${businessUid}/${quotationId}.pdf`;
  const pdfRef = ref(storage, storagePath);
  await uploadBytes(pdfRef, pdfBlob, { contentType: 'application/pdf' });
  const url = await getDownloadURL(pdfRef);
  return url;
}; 